import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { AppSidebar } from '@/components/AppSidebar';
import { SidebarProvider } from '@/components/ui/sidebar';
import { useUserRole } from '@/hooks/useUserRole';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, Eye, Download, Search } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';

interface ContactAccessLog {
  id: string;
  user_email: string;
  user_name: string;
  accessed_at: string;
  contact_count: number;
  access_type: 'view' | 'export' | 'search';
  filter_criteria: any;
}

export default function ContactAccessLogs() {
  const [logs, setLogs] = useState<ContactAccessLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [suspiciousUsers, setSuspiciousUsers] = useState<string[]>([]);
  const { isAdmin } = useUserRole();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isAdmin) {
      navigate('/');
      return;
    }
    fetchLogs();
  }, [isAdmin, navigate]);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      
      // Fetch recent logs (last 7 days)
      const { data, error } = await supabase
        .from('contact_access_logs')
        .select('*')
        .gte('accessed_at', new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString())
        .order('accessed_at', { ascending: false })
        .limit(100);

      if (error) throw error;

      setLogs((data || []).map(log => ({
        ...log,
        access_type: log.access_type as 'view' | 'export' | 'search'
      })));

      // Check for suspicious activity
      const userActivity = new Map<string, number>();
      data?.forEach(log => {
        const count = userActivity.get(log.user_email) || 0;
        userActivity.set(log.user_email, count + 1);
      });

      const suspicious = Array.from(userActivity.entries())
        .filter(([_, count]) => count > 20)
        .map(([email, _]) => email);

      setSuspiciousUsers(suspicious);
    } catch (error) {
      console.error('Error fetching access logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const getAccessTypeBadge = (type: string) => {
    const styles = {
      view: 'bg-blue-100 text-blue-800',
      export: 'bg-red-100 text-red-800',
      search: 'bg-yellow-100 text-yellow-800'
    };
    
    const icons = {
      view: <Eye className="w-3 h-3 mr-1" />,
      export: <Download className="w-3 h-3 mr-1" />,
      search: <Search className="w-3 h-3 mr-1" />
    };

    return (
      <Badge variant="outline" className={styles[type as keyof typeof styles]}>
        {icons[type as keyof typeof icons]}
        {type}
      </Badge>
    );
  };

  const isSuspicious = (userEmail: string) => suspiciousUsers.includes(userEmail);

  if (!isAdmin) return null;

  return (
    <SidebarProvider>
      <div className="flex min-h-screen w-full">
        <AppSidebar onClearData={async () => {}} />
        <main className="flex-1 p-6 space-y-6">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Logs de Acesso aos Contatos</h1>
            <p className="text-muted-foreground mt-2">
              Monitor de segurança para detectar acessos suspeitos à base de contatos
            </p>
          </div>

          {suspiciousUsers.length > 0 && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Atividade Suspeita Detectada</AlertTitle>
              <AlertDescription>
                Os seguintes usuários realizaram mais de 20 acessos nos últimos 7 dias:
                <ul className="mt-2 list-disc list-inside">
                  {suspiciousUsers.map(email => (
                    <li key={email} className="font-mono text-sm">{email}</li>
                  ))}
                </ul>
              </AlertDescription>
            </Alert>
          )}

          <Card>
            <CardHeader>
              <CardTitle>Últimos 100 Acessos</CardTitle>
              <CardDescription>
                Registro dos últimos 7 dias de acesso à base de contatos
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="space-y-2">
                  {[...Array(5)].map((_, i) => (
                    <Skeleton key={i} className="h-12 w-full" />
                  ))}
                </div>
              ) : logs.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">
                  Nenhum log de acesso encontrado nos últimos 7 dias.
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Data/Hora</TableHead>
                        <TableHead>Usuário</TableHead>
                        <TableHead>Tipo</TableHead>
                        <TableHead>Qtd. Contatos</TableHead>
                        <TableHead>Filtros</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {logs.map(log => (
                        <TableRow 
                          key={log.id}
                          className={isSuspicious(log.user_email) ? 'bg-red-50' : ''}
                        >
                          <TableCell className="font-mono text-sm">
                            {format(new Date(log.accessed_at), "dd/MM/yyyy HH:mm:ss", { locale: ptBR })}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              {isSuspicious(log.user_email) && (
                                <AlertTriangle className="w-4 h-4 text-red-600" />
                              )}
                              <div>
                                <div className="font-medium">{log.user_name}</div>
                                <div className="text-xs text-muted-foreground">{log.user_email}</div>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>{getAccessTypeBadge(log.access_type)}</TableCell>
                          <TableCell>
                            <Badge variant="secondary">{log.contact_count}</Badge>
                          </TableCell>
                          <TableCell>
                            {log.filter_criteria && Object.keys(log.filter_criteria).length > 0 ? (
                              <div className="text-xs text-muted-foreground max-w-xs truncate">
                                {JSON.stringify(log.filter_criteria)}
                              </div>
                            ) : (
                              <span className="text-xs text-muted-foreground">Sem filtros</span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </main>
      </div>
    </SidebarProvider>
  );
}
